<template>
  <div class="retention-defaults-by-locale">
    <div class="locale-picker-row">
      <div class="locale-picker-wrap">
        <label class="picker-label">Locale</label>
        <multiselect
          v-model="selectedLocaleOption"
          class="bulk-select locale-multiselect"
          :options="supportedLocales"
          :custom-label="localeLabel"
          track-by="code"
          placeholder="— select a locale —"
          :show-labels="false"
          :allow-empty="false"
          :max-height="360"
        />
      </div>
      <div v-if="selectedLocale && !isLoading" class="locale-stats">
        <strong>{{ configuredCount }}</strong> / {{ totalProducts }}
        products configured for <code>{{ selectedLocale }}</code>
      </div>
    </div>

    <div v-if="isLoading" class="empty">Loading…</div>
    <div v-else-if="totalProducts === 0" class="empty">
      No products found in the catalog for this app.
    </div>

    <template v-else>
      <div class="toolbar">
        <div class="toolbar-row">
          <span class="toolbar-label">Filter:</span>
          <button
            class="filter-btn"
            :class="{ active: filter === 'all' }"
            @click="filter = 'all'"
          >All ({{ totalProducts }})</button>
          <button
            class="filter-btn"
            :class="{ active: filter === 'configured' }"
            @click="filter = 'configured'"
          >Configured ({{ configuredCount }})</button>
          <button
            class="filter-btn"
            :class="{ active: filter === 'unconfigured' }"
            @click="filter = 'unconfigured'"
          >Unconfigured ({{ totalProducts - configuredCount }})</button>
        </div>
        <div class="toolbar-row">
          <span class="toolbar-label">Search:</span>
          <div class="search-input-wrap">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="productSearch"
              type="text"
              class="product-search-input"
              placeholder="Filter product (e.g. .t1, weekly)"
            />
            <i
              v-if="productSearch"
              class="fas fa-times clear-icon"
              @click="productSearch = ''"
            ></i>
          </div>
        </div>
        <div class="toolbar-row">
          <span class="toolbar-label">Selection:</span>
          <button class="link-btn" @click="selectAllVisible">Select all visible</button>
          <button class="link-btn" @click="selectNone">Select none</button>
          <button class="link-btn" @click="selectUnconfigured">Select all unconfigured</button>
          <span class="selection-summary">
            {{ selectedProducts.size }} selected
          </span>
        </div>
      </div>

      <div class="table-wrap">
        <table class="products-table">
          <thead>
            <tr>
              <th class="col-check">
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  :indeterminate.prop="someVisibleSelected"
                  @change="toggleAllVisible($event)"
                />
              </th>
              <th class="col-product">Product ID</th>
              <th class="col-message">Current default for {{ selectedLocale }}</th>
              <th class="col-updated">Updated</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(p, idx) in visibleProducts"
              :key="p.productId"
              class="data-row"
              :class="{ selected: selectedProducts.has(p.productId) }"
              @click="onRowClick(p, idx, $event)"
            >
              <td class="col-check" @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedProducts.has(p.productId)"
                  @change="toggleProduct(p.productId, idx, $event)"
                />
              </td>
              <td><code class="product-id">{{ p.productId }}</code></td>
              <td>
                <template v-if="assignmentFor(p.productId)">
                  <span class="msg-header">{{ messageNameFor(assignmentFor(p.productId).messageId) }}</span>
                  <span
                    class="state-pill"
                    :class="`state-${(messageStateFor(assignmentFor(p.productId).messageId) || 'missing').toLowerCase()}`"
                  >
                    {{ messageStateFor(assignmentFor(p.productId).messageId) || 'missing' }}
                  </span>
                </template>
                <span v-else class="muted">—</span>
              </td>
              <td>
                <span
                  v-if="assignmentFor(p.productId) && assignmentFor(p.productId).configuredAt"
                  class="updated"
                >
                  {{ formatRelative(assignmentFor(p.productId).configuredAt) }}
                </span>
                <span v-else class="muted">—</span>
              </td>
            </tr>
            <tr v-if="visibleProducts.length === 0">
              <td colspan="4" class="empty-row">No products match the current filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Sticky bulk-action bar -->
    <div
      v-if="selectedProducts.size > 0 && !bulkRunning && canConfigureRetentionDefaultMessages"
      class="bulk-bar"
    >
      <span class="bulk-count">
        <strong>{{ selectedProducts.size }}</strong>
        {{ selectedProducts.size === 1 ? 'product' : 'products' }} selected
      </span>
      <span class="bulk-label">Assign to:</span>
      <multiselect
        v-model="bulkSelectedMessage"
        class="bulk-select"
        :options="approvedMessages"
        :custom-label="messageLabel"
        track-by="messageIdentifier"
        placeholder="— pick a message —"
        :show-labels="false"
        :allow-empty="true"
        :max-height="400"
      />
      <button
        class="btn btn-primary"
        @click="apply"
        :disabled="!bulkMessageIdentifier"
      >
        Apply
      </button>
      <div class="bulk-bar-right">
        <button
          v-if="canDeleteRetentionDefaultMessages"
          class="btn btn-remove-default"
          @click="clearAssignments"
        >
          Remove Default Message Assignment
        </button>
      </div>
      <p v-if="approvedMessages.length === 0" class="bulk-hint">
        No APPROVED messages available.
      </p>
    </div>

    <retention-bulk-runner-modals
      unit="product"
      :running="bulkRunning"
      :progress="bulkProgress"
      :show-result="showResultModal"
      :result="bulkResult"
      :running-verb="bulkAction === 'configure' ? 'Assigning' : 'Clearing'"
      :complete-title="bulkAction === 'configure' ? 'Bulk assign' : 'Bulk clear'"
      :retryable-failures="retryableFailures"
      :terminal-failures="invalidProductFailures"
      terminal-label="rejected by Apple"
      @retry="retryFailed"
      @close="closeResultModal"
    >
      <template #terminal="{ failures }">
        <h4><i class="fas fa-info-circle"></i> Rejected by Apple — not retryable</h4>
        <p class="info-text">
          Apple doesn't recognize these product IDs for retention defaults. They've been removed from the
          retention products list and won't be tried again.
        </p>
        <ul>
          <li v-for="f in failures" :key="f.item">
            <code>{{ f.item }}</code>
          </li>
        </ul>
      </template>
    </retention-bulk-runner-modals>
  </div>
</template>

<script>
import { permissionsMixin } from '../../mixins/permissions.mixin';
import { retentionBulkRunnerMixin } from '../../mixins/retentionBulkRunner.mixin';
import RetentionBulkRunnerModals from '../../components/Retention/RetentionBulkRunnerModals.vue';
import { SUPPORTED_LOCALES, localeLabel } from '../../constants/retentionLocales';
import notify from '../../util/notification';

export default {
  mixins: [permissionsMixin, retentionBulkRunnerMixin],
  components: { RetentionBulkRunnerModals },
  data() {
    const fallbackLocale = SUPPORTED_LOCALES[0]?.code || 'en-US';
    return {
      supportedLocales: SUPPORTED_LOCALES,
      selectedLocale: fallbackLocale,
      selectedProducts: new Set(),
      lastClickedIndex: null,
      filter: 'all',
      productSearch: '',
      bulkMessageIdentifier: null,
      bulkAction: 'configure',
      bulkLastMessageIdentifier: null
    };
  },
  computed: {
    products() {
      return this.$store.getters['retention/productIds'] || [];
    },
    defaults() {
      return this.$store.getters['retention/defaults'] || [];
    },
    retentionMessages() {
      return this.$store.getters.retentionMessages || [];
    },
    approvedMessages() {
      return this.$store.getters['retention/defaultEligibleMessages'] || [];
    },
    bulkSelectedMessage: {
      get() {
        return (
          this.approvedMessages.find(
            m => m.messageIdentifier === this.bulkMessageIdentifier
          ) || null
        );
      },
      set(m) {
        this.bulkMessageIdentifier = m ? m.messageIdentifier : null;
      }
    },
    selectedLocaleOption: {
      get() {
        return this.supportedLocales.find(l => l.code === this.selectedLocale) || null;
      },
      set(l) {
        if (!l || l.code === this.selectedLocale) return;
        this.selectedLocale = l.code;
        this.onLocaleChange();
      }
    },
    isLoading() {
      return this.$store.getters['retention/isLoadingDefaults'];
    },
    totalProducts() {
      return this.products.length;
    },
    assignmentByProduct() {
      const map = new Map();
      for (const d of this.defaults) {
        if (d.locale === this.selectedLocale) map.set(d.productId, d);
      }
      return map;
    },
    configuredCount() {
      // Count only catalog products that have an assignment. Raw default records can
      // include orphans (products no longer in the catalog), which would otherwise push
      // the count above the product total and yield a negative "unconfigured".
      return this.products.filter(p => this.assignmentByProduct.has(p.productId)).length;
    },
    sortedProducts() {
      return [...this.products].sort((a, b) => a.productId.localeCompare(b.productId));
    },
    visibleProducts() {
      let list = this.sortedProducts.slice();
      if (this.filter === 'configured') {
        list = list.filter(p => this.assignmentByProduct.has(p.productId));
      } else if (this.filter === 'unconfigured') {
        list = list.filter(p => !this.assignmentByProduct.has(p.productId));
      }
      const q = this.productSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(p => p.productId.toLowerCase().includes(q));
      }
      return list;
    },
    allVisibleSelected() {
      return (
        this.visibleProducts.length > 0 &&
        this.visibleProducts.every(p => this.selectedProducts.has(p.productId))
      );
    },
    someVisibleSelected() {
      const some = this.visibleProducts.some(p => this.selectedProducts.has(p.productId));
      return some && !this.allVisibleSelected;
    }
  },
  watch: {
    '$route.query.locale': {
      immediate: false,
      handler(newLoc) {
        if (
          newLoc &&
          newLoc !== this.selectedLocale &&
          this.supportedLocales.find(l => l.code === newLoc)
        ) {
          this.selectedLocale = newLoc;
          this.selectNone();
          this.bulkMessageIdentifier = null;
        }
      }
    }
  },
  mounted() {
    const queryLocale = this.$route.query.locale;
    if (queryLocale && this.supportedLocales.find(l => l.code === queryLocale)) {
      this.selectedLocale = queryLocale;
    } else {
      this.syncLocaleToUrl(this.selectedLocale);
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.bulkRunning) {
      const ok = confirm('A bulk operation is still running. Leave anyway?');
      if (!ok) return next(false);
    }
    next();
  },
  methods: {
    syncLocaleToUrl(locale) {
      const nextQuery = { ...this.$route.query, view: 'locale', locale };
      this.$router.replace({ query: nextQuery }).catch(() => {});
    },
    onLocaleChange() {
      this.selectNone();
      this.bulkMessageIdentifier = null;
      this.syncLocaleToUrl(this.selectedLocale);
    },
    assignmentFor(productId) {
      return this.assignmentByProduct.get(productId) || null;
    },
    messageNameFor(messageId) {
      const m = this.retentionMessages.find(msg => msg.id === messageId);
      return m?.name || m?.header || '(message not found)';
    },
    messageStateFor(messageId) {
      const m = this.retentionMessages.find(msg => msg.id === messageId);
      return m?.messageState || null;
    },
    localeLabel,
    messageLabel(m) {
      return m.name || m.header || '(unnamed)';
    },
    formatRelative(ts) {
      if (!ts) return '—';
      const now = Date.now();
      const diff = now - ts;
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'just now';
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      const d = new Date(ts);
      return d.toLocaleDateString();
    },
    onRowClick(p, idx, event) {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'A') return;
      this.toggleProduct(p.productId, idx, event);
    },
    toggleProduct(productId, idx, event) {
      if (event && event.shiftKey && this.lastClickedIndex !== null) {
        const [from, to] = [this.lastClickedIndex, idx].sort((a, b) => a - b);
        const target = !this.selectedProducts.has(productId);
        const next = new Set(this.selectedProducts);
        for (let i = from; i <= to; i++) {
          const pid = this.visibleProducts[i]?.productId;
          if (!pid) continue;
          if (target) next.add(pid);
          else next.delete(pid);
        }
        this.selectedProducts = next;
      } else {
        const next = new Set(this.selectedProducts);
        if (next.has(productId)) next.delete(productId);
        else next.add(productId);
        this.selectedProducts = next;
      }
      this.lastClickedIndex = idx;
    },
    toggleAllVisible(event) {
      const target = event.target.checked;
      const next = new Set(this.selectedProducts);
      for (const p of this.visibleProducts) {
        if (target) next.add(p.productId);
        else next.delete(p.productId);
      }
      this.selectedProducts = next;
    },
    selectAllVisible() {
      const next = new Set(this.selectedProducts);
      for (const p of this.visibleProducts) next.add(p.productId);
      this.selectedProducts = next;
    },
    selectNone() {
      this.selectedProducts = new Set();
      this.lastClickedIndex = null;
    },
    selectUnconfigured() {
      const next = new Set(this.selectedProducts);
      for (const p of this.products) {
        if (!this.assignmentByProduct.has(p.productId)) next.add(p.productId);
      }
      this.selectedProducts = next;
    },
    async apply() {
      if (!this.bulkMessageIdentifier || this.selectedProducts.size === 0) return;
      const productIds = Array.from(this.selectedProducts);
      this.bulkLastMessageIdentifier = this.bulkMessageIdentifier;
      await this.runBulk({
        productIds,
        messageIdentifier: this.bulkMessageIdentifier,
        action: 'configure'
      });
    },
    async clearAssignments() {
      if (this.selectedProducts.size === 0) return;
      const onlyConfigured = Array.from(this.selectedProducts).filter(pid =>
        this.assignmentByProduct.has(pid)
      );
      if (onlyConfigured.length === 0) {
        notify('None of the selected products have a default for this locale.', 'info');
        return;
      }
      if (!confirm(
        `Clear ${onlyConfigured.length} default assignment(s) for ${this.selectedLocale}? Underlying messages stay intact.`
      )) {
        return;
      }
      this.bulkLastMessageIdentifier = null;
      await this.runBulk({
        productIds: onlyConfigured,
        messageIdentifier: null,
        action: 'remove'
      });
    },
    async retryFailed() {
      // Only retry the retryable bucket — invalid products (4000023) would just
      // hit the same wall and have already been blacklisted.
      const productIds = this.retryableFailures.map(f => f.item);
      if (productIds.length === 0) return;
      this.showResultModal = false;
      await this.runBulk({
        productIds,
        messageIdentifier: this.bulkLastMessageIdentifier,
        action: this.bulkAction
      });
    },
    async runBulk({ productIds, messageIdentifier, action }) {
      this.bulkAction = action;
      const locale = this.selectedLocale;
      const worker = async (productId) => {
        if (action === 'configure') {
          await this.$store.dispatch('retention/configureDefault', {
            productId, locale, messageIdentifier, silent: true
          });
        } else {
          await this.$store.dispatch('retention/removeDefault', {
            productId, locale, silent: true
          });
        }
      };

      await this.executeBulk({ items: productIds, worker });

      const failed = this.bulkResult.failures.length;
      const ok = this.bulkResult.successes.length;
      const verb = action === 'configure' ? 'assigned' : 'cleared';
      if (failed === 0) {
        notify(`Bulk ${verb}: ${ok} of ${productIds.length} succeeded.`, 'success');
      } else {
        notify(`Bulk ${verb}: ${ok} succeeded, ${failed} failed.`, 'error');
      }

      // Products Apple rejected as invalid (4000023) get blacklisted server-side
      // and dropped from the catalog. Force-refetch so the local list updates —
      // the store caches productIds per-app, so without `force` it would return
      // the stale list (still containing the just-blacklisted products) and they'd
      // keep getting picked and re-sent for other locales.
      const blacklisted = this.invalidProductFailures;
      if (blacklisted.length > 0) {
        await this.$store.dispatch('retention/fetchProducts', { force: true });
        notify(
          `${blacklisted.length} product(s) flagged invalid by Apple, and removed from the retention catalog.`,
          'info'
        );
      }

      // Bulk run finished — always clear the selection and the pending message
      // pick so the next batch starts from a clean slate, regardless of whether
      // the run fully or partially succeeded. Failed items can still be retried
      // from the result modal (retryableFailures), which doesn't read the selection.
      this.selectNone();
      this.bulkMessageIdentifier = null;
    },
    closeResultModal() {
      this.closeBulkResultModal();
    }
  }
};
</script>

<style scoped>
.retention-defaults-by-locale {
  flex: 1;
  min-width: 0;
  padding-bottom: 6rem;
  color: rgba(255, 255, 255, 0.88);
}

.locale-picker-row {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
  padding: 0.75rem 0.85rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.locale-picker-wrap {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.picker-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.locale-stats {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
}

.locale-stats strong {
  color: #03B3DA;
  font-variant-numeric: tabular-nums;
}

.locale-stats code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  background: rgba(255, 255, 255, 0.06);
  padding: 1px 6px;
  border-radius: 4px;
  color: #fff;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.toolbar {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.toolbar-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.filter-btn {
  padding: 4px 11px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.filter-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.filter-btn.active {
  background: #03B3DA;
  border-color: #03B3DA;
  color: #fff;
}

.search-input-wrap {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.search-input-wrap .search-icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.78rem;
  pointer-events: none;
}

.product-search-input {
  width: 100%;
  padding: 0.4rem 1.85rem 0.4rem 1.9rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.85rem;
}

.product-search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.product-search-input:focus {
  outline: none;
  border-color: #03B3DA;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 3px rgba(3, 179, 218, 0.15);
}

.search-input-wrap .clear-icon {
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  cursor: pointer;
}

.search-input-wrap .clear-icon:hover {
  color: #fff;
}

.link-btn {
  background: none;
  border: none;
  color: #03B3DA;
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
}

.link-btn:hover { background: rgba(3, 179, 218, 0.1); }

.selection-summary {
  margin-left: auto;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
}

.table-wrap {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  color: rgba(255, 255, 255, 0.85);
}

.products-table th,
.products-table td {
  text-align: left;
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.88rem;
  vertical-align: middle;
}

.products-table thead th {
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.products-table tr.data-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.products-table tr.data-row:hover { background: rgba(3, 179, 218, 0.06); }
.products-table tr.data-row.selected { background: rgba(3, 179, 218, 0.12); }
.products-table tr.data-row.selected:hover { background: rgba(3, 179, 218, 0.18); }

.products-table tr:last-child td { border-bottom: none; }

.products-table input[type="checkbox"] {
  cursor: pointer;
  accent-color: #03B3DA;
}

.col-check { width: 36px; text-align: center; }
.col-product { width: 30%; }
.col-message { width: auto; }
.col-updated { width: 130px; }

.product-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.83rem;
  color: rgba(255, 255, 255, 0.92);
  background: none;
}

.msg-header {
  display: inline-block;
  margin-right: 0.5rem;
  color: rgba(255, 255, 255, 0.95);
}

.state-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.state-draft     { background: rgba(255, 255, 255, 0.1);  color: #c8d0d9; }
.state-pending   { background: rgba(255, 192, 50, 0.15);  color: #ffc832; }
.state-approved  { background: rgba(46, 204, 113, 0.18);  color: #2ecc71; }
.state-rejected  { background: rgba(231, 76, 60, 0.18);   color: #e74c3c; }
.state-missing,
.state-unknown   { background: rgba(255, 255, 255, 0.07); color: rgba(255, 255, 255, 0.5); }

.muted { color: rgba(255, 255, 255, 0.35); }

.updated {
  font-size: 0.84rem;
  color: rgba(255, 255, 255, 0.6);
}

.empty-row {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  padding: 1.5rem;
  font-style: italic;
}

/* Sticky bulk action bar */
.bulk-bar {
  position: fixed;
  bottom: 0;
  left: 260px;
  right: 0;
  background: rgba(20, 25, 38, 0.96);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(6px);
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 200;
  flex-wrap: wrap;
}

.bulk-count {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.85);
}
.bulk-count strong { color: #03B3DA; }

.bulk-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.bulk-select {
  width: 340px;
  min-width: 340px;
  font-size: 0.85rem;
  color: #fff;
}

/* vue-multiselect dark theme overrides (scoped -> ::v-deep) */
.bulk-select ::v-deep .multiselect__tags {
  min-height: 36px;
  padding: 6px 40px 0 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
}
.bulk-select ::v-deep .multiselect__single,
.bulk-select ::v-deep .multiselect__input {
  background: transparent;
  color: #fff;
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding: 0;
}
.bulk-select ::v-deep .multiselect__placeholder {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding-top: 0;
}
.bulk-select ::v-deep .multiselect--active .multiselect__tags {
  border-color: #03B3DA;
  box-shadow: 0 0 0 0.15rem rgba(3, 179, 218, 0.2);
}
.bulk-select ::v-deep .multiselect__content-wrapper {
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.bulk-select ::v-deep .multiselect__option {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  white-space: normal;
  min-height: unset;
  padding: 8px 12px;
}
.bulk-select ::v-deep .multiselect__option--highlight,
.bulk-select ::v-deep .multiselect__option--highlight::after {
  background: #03B3DA;
  color: #fff;
}
.bulk-select ::v-deep .multiselect__option--selected {
  background: rgba(3, 179, 218, 0.18);
  color: #fff;
  font-weight: 600;
}
.bulk-select ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #03B3DA;
}

.bulk-hint {
  width: 100%;
  margin: 0.4rem 0 0;
  font-size: 0.8rem;
  color: #ffb84d;
}

.btn {
  padding: 0.45rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.btn-primary {
  background: #03B3DA;
  color: #fff;
}
.btn-primary:hover { background: #04a3c5; }
.btn-primary:disabled {
  background: rgba(3, 179, 218, 0.35);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}
/* Pushes the destructive action to the far right, away from Apply. */
.bulk-bar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
/* Custom danger button — avoids the global template's .btn-danger gradient that
   washed out the red label. Solid red on hover keeps the text readable. */
.btn-remove-default {
  background: rgba(231, 76, 60, 0.12);
  color: #ff6b5e;
  border: 1px solid rgba(231, 76, 60, 0.5);
}
.btn-remove-default:hover {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}
</style>
